/**
 * @file        src/components/PasswordConfirmModal.jsx
 * @description Shared "confirm this action with your own login password"
 *              modal — the same re-authentication pattern already used by
 *              Wallet Management (credit/refund). Any action that must be
 *              re-verified server-side (save/delete SMTP credentials, etc.)
 *              opens this with an `onConfirm(password)` callback; the modal
 *              stays open and shows the error inline on failure.
 * @author      Kinetika Labs
 * @created     2026-08-05
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-08-05 Claude — Initial, factored out for Email Settings (save +
 *                          delete) so it isn't reimplemented a third time.
 *   - 2026-09-17 Claude — The re-auth field is now <PasswordInput>. An admin
 *                          who mistypes here learns of it from a rejected
 *                          action, so it above all needs a reveal toggle.
 */
import { useState } from 'react';
import Icon from './Icon';
import PasswordInput from './PasswordInput';

/**
 * @param {string} title
 * @param {string} [description]
 * @param {string} [confirmLabel]
 * @param {boolean} [danger] - styles the confirm button as destructive.
 * @param {(password: string) => Promise<void>} onConfirm - throw/reject to keep the modal open with an error.
 * @param {() => void} onClose
 */
export default function PasswordConfirmModal({
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  onConfirm,
  onClose,
}) {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!password) { setErr('Your password is required.'); return; }
    setBusy(true);
    try {
      await onConfirm(password);
    } catch (e2) {
      const apiErr = e2?.response?.data?.error;
      const message = typeof apiErr === 'string' ? apiErr : apiErr?.message;
      if (apiErr?.code === 'BAD_CREDENTIALS') setErr('Incorrect password.');
      else setErr(message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 440 }}>
        <div className="lk-modal-head">
          <h3>{title}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form className="det-body" onSubmit={submit}>
          {description ? <p className="muted">{description}</p> : null}

          <div className="field full">
            <label>Confirm your password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your admin password"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore
              autoFocus
            />
          </div>

          {err ? <div className="field-error">{err}</div> : null}

          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={`btn ${danger ? 'danger' : 'primary'}`} disabled={busy}>
              {busy ? 'Please wait…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
